import React, { useEffect, useMemo, useState } from 'react';
import { X, Calendar as CalIcon, Clock, Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';

// Free-call booking modal: pick a weekday slot (already-booked times are
// disabled from /api/booking/availability), leave your details, and Kareem
// gets emailed at kareem@bluprise.com to call you back. Times shown are the
// visitor's local time; the chosen slot is stored to prevent double-booking.

const SLOT_TIMES = ['10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'];
const DAYS_AHEAD = 21;

function pad(n) { return String(n).padStart(2, '0'); }
function dayKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

function buildDays() {
  const out = [];
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  for (let i = 1; i <= DAYS_AHEAD; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue; // weekdays only
    out.push(d);
  }
  return out;
}

const BookCallModal = ({ open, onClose }) => {
  const [step, setStep] = useState('date'); // date | time | details | done
  const [days] = useState(buildDays);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booked, setBooked] = useState(new Set());
  const [form, setForm] = useState({ name: '', email: '', phone: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep('date'); setSelectedDay(null); setSelectedSlot(null); setError(null);
    setForm({ name: '', email: '', phone: '', description: '' });
    let cancelled = false;
    fetch('/api/booking/availability', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { booked: [] }))
      .then((d) => { if (!cancelled) setBooked(new Set(d.booked || [])); })
      .catch(() => { if (!cancelled) setBooked(new Set()); });
    return () => { cancelled = true; };
  }, [open]);

  const now = useMemo(() => new Date(), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const slotKey = (day, time) => `${dayKey(day)}T${time}`;
  const isPast = (day, time) => {
    const [h, m] = time.split(':').map(Number);
    const dt = new Date(day); dt.setHours(h, m, 0, 0);
    return dt.getTime() <= now.getTime();
  };

  const fmtDay = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Please fill in your name, email, and phone.');
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const res = await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          slot: slotKey(selectedDay, selectedSlot),
          slotLabel: `${fmtDay(selectedDay)} at ${selectedSlot}`,
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          description: form.description.trim(),
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not book that time. Please pick another slot.');
      setStep('done');
    } catch (err) {
      setError(err.message);
      if (err.message && err.message.toLowerCase().includes('already')) {
        setBooked((b) => new Set(b).add(slotKey(selectedDay, selectedSlot)));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full bg-zinc-800/80 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-950 shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-white/10 bg-zinc-950/95 backdrop-blur">
          <div className="flex items-center gap-2">
            {step !== 'date' && step !== 'done' && (
              <button onClick={() => setStep(step === 'details' ? 'time' : 'date')} className="text-zinc-400 hover:text-white p-1 -ml-1">
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <h3 className="text-white font-semibold">Book a free call</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-6">
          {step === 'date' && (
            <>
              <p className="text-sm text-zinc-400 mb-4 flex items-center gap-2"><CalIcon className="h-4 w-4 text-blue-400" /> Pick a day that works for you.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {days.map((d) => (
                  <button key={dayKey(d)} onClick={() => { setSelectedDay(d); setStep('time'); }}
                    className="rounded-lg border border-white/10 bg-zinc-900/60 px-3 py-3 text-sm text-white hover:border-blue-500/60 hover:bg-zinc-900 transition-colors">
                    {fmtDay(d)}
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 'time' && selectedDay && (
            <>
              <p className="text-sm text-zinc-400 mb-4 flex items-center gap-2"><Clock className="h-4 w-4 text-blue-400" /> {fmtDay(selectedDay)} — choose a time <span className="text-zinc-600">({Intl.DateTimeFormat().resolvedOptions().timeZone})</span></p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {SLOT_TIMES.map((t) => {
                  const disabled = booked.has(slotKey(selectedDay, t)) || isPast(selectedDay, t);
                  return (
                    <button key={t} disabled={disabled} onClick={() => { setSelectedSlot(t); setStep('details'); }}
                      className={`rounded-lg border px-2 py-2 text-sm transition-colors ${
                        disabled ? 'border-white/5 bg-zinc-900/40 text-zinc-600 line-through cursor-not-allowed'
                          : 'border-white/10 bg-zinc-900/60 text-white hover:border-blue-500/60 hover:bg-zinc-900'}`}>
                      {t}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === 'details' && (
            <form onSubmit={submit} className="space-y-3">
              <div className="rounded-lg bg-blue-500/10 border border-blue-400/20 px-3 py-2 text-sm text-blue-200 mb-1">
                {fmtDay(selectedDay)} at {selectedSlot}
              </div>
              <input className={inputCls} placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <input className={inputCls} placeholder="Phone number *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <textarea className={`${inputCls} min-h-[90px] resize-y`} placeholder="What would you like to discuss?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button type="submit" disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 px-6 py-3 rounded-lg text-sm font-medium text-white transition-colors">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Booking…</> : 'Confirm booking'}
              </button>
              <p className="text-center text-xs text-zinc-500">Kareem will call you back at your selected time.</p>
            </form>
          )}

          {step === 'done' && (
            <div className="text-center py-8">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">You're booked!</h4>
              <p className="text-sm text-zinc-400 mb-1">{fmtDay(selectedDay)} at {selectedSlot}</p>
              <p className="text-sm text-zinc-400">Kareem will call you at <span className="text-white">{form.phone}</span> to discuss your project.</p>
              <button onClick={onClose} className="mt-6 bg-white/10 hover:bg-white/20 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition-colors">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookCallModal;
